/**
 * Roving Tabindex Hook
 *
 * Implements WAI-ARIA roving tabindex pattern for toolbar keyboard navigation.
 * Only one element in the group has tabIndex=0 (the focused one), all others have tabIndex=-1.
 * Arrow keys move focus between elements, Tab enters/exits the group.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface UseRovingTabindexOptions {
  /** Ref to the toolbar container element */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Whether keyboard navigation is enabled */
  enabled?: boolean;
  /** Orientation for arrow key mapping (default: horizontal) */
  orientation?: "horizontal" | "vertical";
  /** Whether to wrap around at ends (default: true) */
  wrap?: boolean;
  /** Selector for items (default: button). Navigation skips disabled items. */
  itemSelector?: string;
}

/**
 * Hook for implementing roving tabindex pattern in toolbars.
 *
 * Usage:
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { getTabIndex, handleKeyDown } = useRovingTabindex({ containerRef });
 *
 * return (
 *   <div ref={containerRef} onKeyDown={handleKeyDown}>
 *     <button tabIndex={getTabIndex(0)}>First</button>
 *     <button tabIndex={getTabIndex(1)}>Second</button>
 *   </div>
 * );
 * ```
 */
export function useRovingTabindex({
  containerRef,
  enabled = true,
  orientation = "horizontal",
  wrap = true,
  itemSelector = "button",
}: UseRovingTabindexOptions) {
  // Track the focused item index (persists across re-renders)
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Cache of focusable items refs for O(1) access
  const itemsRef = useRef<HTMLElement[]>([]);

  /**
   * Get all focusable items in the container.
   * Called on each keydown to ensure we have fresh list (handles dynamic buttons).
   */
  const getFocusableItems = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];
    return Array.from(containerRef.current.querySelectorAll<HTMLElement>(itemSelector));
  }, [containerRef, itemSelector]);

  /**
   * Update the cached items list when container changes.
   */
  useEffect(() => {
    if (containerRef.current) {
      itemsRef.current = getFocusableItems();
    }
  }, [containerRef, getFocusableItems]);

  /**
   * Find the next focusable index, skipping disabled items.
   */
  const findNextIndex = useCallback(
    (currentIndex: number, direction: 1 | -1, items: HTMLElement[]): number => {
      const count = items.length;
      if (count === 0) return 0;

      let nextIndex = currentIndex;
      let attempts = 0;

      // Loop until we find an enabled item or exhaust all options
      do {
        if (wrap) {
          nextIndex = (nextIndex + direction + count) % count;
        } else {
          nextIndex = Math.max(0, Math.min(count - 1, nextIndex + direction));
          // If we hit the edge without wrap, stop
          if (
            (direction === 1 && nextIndex === count - 1) ||
            (direction === -1 && nextIndex === 0)
          ) {
            break;
          }
        }
        attempts++;
      } while (
        items[nextIndex]?.getAttribute("disabled") !== null &&
        attempts < count
      );

      return nextIndex;
    },
    [wrap]
  );

  /**
   * Handle keyboard events for arrow navigation.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!enabled) return;

      const items = getFocusableItems();
      if (items.length === 0) return;

      // Find current focus position
      const activeElement = document.activeElement;
      let currentIndex = items.indexOf(activeElement as HTMLElement);
      if (currentIndex === -1) {
        // Focus is not in the toolbar, use stored index
        currentIndex = Math.min(focusedIndex, items.length - 1);
      }

      const prevKey = orientation === "horizontal" ? "ArrowLeft" : "ArrowUp";
      const nextKey = orientation === "horizontal" ? "ArrowRight" : "ArrowDown";

      let newIndex: number | null = null;

      switch (event.key) {
        case nextKey:
          newIndex = findNextIndex(currentIndex, 1, items);
          break;
        case prevKey:
          newIndex = findNextIndex(currentIndex, -1, items);
          break;
        case "Home":
          // Find first non-disabled item
          newIndex = findNextIndex(items.length - 1, 1, items);
          break;
        case "End":
          // Find last non-disabled item
          newIndex = findNextIndex(0, -1, items);
          break;
        default:
          return; // Don't handle other keys
      }

      if (newIndex !== null && newIndex !== currentIndex) {
        event.preventDefault();
        setFocusedIndex(newIndex);
        items[newIndex]?.focus();
      }
    },
    [enabled, getFocusableItems, focusedIndex, orientation, findNextIndex]
  );

  /**
   * Get tabIndex for an item at a given position.
   * First item (or last focused item) gets 0, others get -1.
   */
  const getTabIndex = useCallback(
    (index: number): 0 | -1 => {
      return index === focusedIndex ? 0 : -1;
    },
    [focusedIndex]
  );

  /**
   * Handle focus events from buttons to keep focusedIndex in sync.
   * This ensures clicking a button also updates the roving index.
   */
  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  /**
   * Ensure focusedIndex is valid when items change dynamically.
   */
  useEffect(() => {
    const items = getFocusableItems();
    if (focusedIndex >= items.length && items.length > 0) {
      setFocusedIndex(items.length - 1);
    }
  }, [getFocusableItems, focusedIndex]);

  return {
    /** Get tabIndex for an item at the given index */
    getTabIndex,
    /** Keyboard event handler - attach to container */
    handleKeyDown,
    /** Focus handler - attach to each item's onFocus with its index */
    handleFocus,
    /** Current focused index (read-only) */
    focusedIndex,
  };
}
